import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './PlayerPredictions.css';

/**
 * PlayerPredictions — tips-sektionen på spelarprofilen.
 *
 * Visar totalpoäng, träffsäkerhet och antal rättade tips plus de senaste
 * tipsen (med facit eller "väntar på rättning"). Sektionen döljs helt när
 * spelaren aldrig tippat — profiler ska inte fyllas med tomma kort.
 *
 * På den egna profilen visas dessutom plånboken: spenderbart saldo och
 * transaktionshistorik (JWT-gated i API:t — andras plånböcker går inte att
 * hämta, så blocket renderas bara när steamid64 matchar inloggad användare).
 *
 * @param {{steamid64: string, onSelectMatch?: function}} props Spelarens
 *   steamid64 och klick-callback till matchsidan.
 * @returns {JSX.Element|null} Sektionen, eller null utan tips.
 */
const PlayerPredictions = ({ steamid64, onSelectMatch }) => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [wallet, setWallet] = useState(null);
  const isOwnProfile = Boolean(user?.steam_id && user.steam_id === steamid64);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/predictions/player/${steamid64}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled && body && (body.recent || []).length > 0) setData(body);
      })
      .catch(() => {
        // tyst — tips-sektionen är icke-kritisk för profilen
      });
    return () => { cancelled = true; };
  }, [steamid64]);

  useEffect(() => {
    if (!isOwnProfile) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/predictions/wallet`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (!cancelled && body) setWallet(body); })
      .catch(() => {
        // tyst — plånboken är icke-kritisk för profilen
      });
    return () => { cancelled = true; };
  }, [isOwnProfile, getAuthHeaders]);

  if (!data && !(isOwnProfile && wallet)) return null;

  /**
   * Facit-etikett för ett tips: poäng, eller väntar-status för orättade.
   *
   * @param {object} prediction Tips-rad från API:t.
   * @returns {JSX.Element} Resultat-span med färgklass.
   */
  const renderResult = (prediction) => {
    if (prediction.points_awarded === null || prediction.points_awarded === undefined) {
      return <span className="pred-result pending">⏳ {t('predictions.pendingResult')}</span>;
    }
    if (prediction.is_correct) {
      return <span className="pred-result correct">✅ +{prediction.points_awarded} {t('predictions.points')}</span>;
    }
    return (
      <span className="pred-result wrong">
        ❌ {prediction.points_awarded < 0 ? prediction.points_awarded : 0} {t('predictions.points')}
      </span>
    );
  };

  /**
   * Visningsnamn för en transaktionstyp i plånbokshistoriken.
   *
   * @param {string} kind Ledger-kind från API:t (prediction, welcome_bonus …).
   * @returns {string} Lokaliserad etikett, eller rå kind som fallback.
   */
  const walletKindLabel = (kind) => {
    const key = {
      prediction: 'wallet.kindPrediction',
      welcome_bonus: 'wallet.kindWelcomeBonus',
      shop_purchase: 'wallet.kindShopPurchase',
      raffle_ticket: 'wallet.kindRaffleTicket',
      stake: 'wallet.kindStake',
      payout: 'wallet.kindPayout',
      refund: 'wallet.kindRefund',
      admin_adjust: 'wallet.kindAdminAdjust',
    }[kind];
    return key ? t(key) : kind;
  };

  return (
    <div className="player-predictions">
      <h2 className="section-title section-heading">🔮 {t('predictions.profileTitle')}</h2>
      {isOwnProfile && wallet && (
        <div className="pred-wallet">
          <div className="pred-wallet-header">
            <span className="pred-wallet-title">🪙 {t('wallet.title')}</span>
            <span className="pred-wallet-balance">
              {wallet.balance.toLocaleString(currentLocale())} {t('predictions.points')}
            </span>
          </div>
          {(wallet.transactions || []).length > 0 && (
            <div className="pred-wallet-list">
              {wallet.transactions.slice(0, 10).map((txn, index) => (
                <div key={`${txn.created_at}-${index}`} className="pred-wallet-row">
                  <span className="pred-wallet-kind">
                    {walletKindLabel(txn.kind)}
                    {txn.note && <span className="pred-wallet-note">{txn.note}</span>}
                  </span>
                  <span className={`pred-wallet-amount ${txn.amount >= 0 ? 'positive' : 'negative'}`}>
                    {txn.amount >= 0 ? '+' : ''}{txn.amount}
                  </span>
                  <span className="pred-wallet-after">
                    = {txn.balance_after.toLocaleString(currentLocale())}
                  </span>
                  <span className="pred-wallet-date">
                    {new Date(txn.created_at).toLocaleDateString(currentLocale())}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {data && (
      <>
      <div className="pred-summary-row">
        <div className="pred-summary-stat">
          <span className="pred-summary-value">{data.points}</span>
          <span className="pred-summary-label">{t('predictions.points')}</span>
        </div>
        <div className="pred-summary-stat">
          <span className="pred-summary-value">{Math.round(data.accuracy * 100)}%</span>
          <span className="pred-summary-label">{t('predictions.profileAccuracy')}</span>
        </div>
        <div className="pred-summary-stat">
          <span className="pred-summary-value">{data.correct}/{data.predictions}</span>
          <span className="pred-summary-label">{t('predictions.profileCorrect')}</span>
        </div>
      </div>
      <div className="pred-recent-list">
        {data.recent.map((prediction) => {
          const marketKey = {
            winner: 'predictions.marketWinner',
            top_fragger: 'predictions.marketTopFragger',
            top_adr: 'predictions.marketTopAdr',
            series_score: 'predictions.marketSeriesScore',
            pistol_round: 'predictions.marketPistol',
          }[prediction.market] || null;
          const pickedName = prediction.pick_label
            || (prediction.picked_team === 'team1' ? prediction.team1_name : prediction.team2_name);
          return (
            <div
              key={`${prediction.match_id}-${prediction.market || 'winner'}`}
              className="pred-recent-row clickable"
              onClick={() => onSelectMatch && onSelectMatch(prediction.match_id)}
            >
              <span className="pred-recent-match">
                {prediction.team1_name} vs {prediction.team2_name}
              </span>
              <span className="pred-recent-pick">
                {marketKey ? t(marketKey) : t('predictions.yourPick')}: <strong>{pickedName}</strong>
                {prediction.odds_snapshot !== null && prediction.odds_snapshot !== undefined && (
                  <span className="pred-recent-odds">
                    ({Math.round(prediction.odds_snapshot * 100)}%)
                  </span>
                )}
              </span>
              {renderResult(prediction)}
              <span className="pred-recent-date">
                {new Date(prediction.created_at).toLocaleDateString(currentLocale())}
              </span>
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
};

export default PlayerPredictions;
